/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/validators.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Validation utilities for Users module.
────────────────────────────────────────────────────────────────*/

export type FormErrors = Record<string, string>;

import { UserFormData } from '../hooks/useUserForm';

/**
 * Validate user form data
 */
export function validateUserForm(
  formData: UserFormData,
  mode: 'create' | 'edit',
  t?: (key: string) => string,
): FormErrors {
  const errors: FormErrors = {};
  const translate = (key: string, defaultValue: string) => {
    if (t) {
      try {
        const translated = t(key);
        // If translation returns the key itself (no translation found), use default
        return translated !== key ? translated : defaultValue;
      } catch {
        return defaultValue;
      }
    }
    return defaultValue;
  };

  if (!formData.username || !formData.username.trim()) {
    errors.username = translate('users.validation.usernameRequired', 'Username is required');
  }

  if (!formData.email || !formData.email.trim()) {
    errors.email = translate('users.validation.emailRequired', 'Email is required');
  } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
    errors.email = translate('users.validation.emailInvalid', 'Email is invalid');
  }

  if (!formData.locale) {
    errors.locale = translate('users.validation.localeRequired', 'Default Language UI is required');
  }

  if (!formData.startupDialog) {
    errors.startupDialog = translate('users.validation.startupDialogRequired', 'Startup Dialog is required');
  }

  const coreEnabled = formData.appsConfigs?.coreEnabled ?? true;
  if (coreEnabled) {
    if (!formData.profileId && !formData.isPrimaryAdmin) {
      errors.profileId = translate('users.validation.profileIdRequired', 'Role Profile is required for Ring2All');
    }
    if (!formData.logProfileId) {
      errors.logProfileId = translate('users.validation.logProfileIdRequired', 'Log Profile is required for Ring2All');
    }
  }

  if (formData.appsConfigs?.switchboardEnabled) {
    if (!formData.appsConfigs.switchboardRole) {
      errors.switchboardRole = translate('users.validation.switchboardRoleRequired', 'Role is required for Switchboard');
    }
    if (!formData.appsConfigs.switchboardLogProfileId) {
      errors.switchboardLogProfileId = translate('users.validation.switchboardLogProfileIdRequired', 'Log Profile is required for Switchboard');
    }
  }

  if (mode === 'create') {
    if (!formData.password || !formData.password.trim()) {
      errors.password = translate('users.validation.passwordRequired', 'Password is required');
    } else if (formData.password.trim().length < 8) {
      errors.password = translate('users.validation.passwordMinLength', 'Password must contain at least 8 characters');
    }
  }

  return errors;
}

